import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import type { ReactNode } from "react";
import {
  type PlaceAddress,
  PlacesAutocomplete,
} from "@/components/ui/places-autocomplete";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";

/**
 * A resolved address split across the field names the place forms use.
 * Coordinates are strings because `Place.latitude` / `Place.longitude` are TEXT;
 * `""` means Google did not resolve that part.
 */
export type PlaceAddressFormValues = {
  streetAddress: string;
  streetAddressLine2: string;
  city: string;
  state: string;
  zip: string;
  latitude: string;
  longitude: string;
};

/** Maps a picked suggestion onto the place forms' field names. */
export function toPlaceAddressFormValues(
  address: PlaceAddress,
): PlaceAddressFormValues {
  return {
    streetAddress: address.streetAddress,
    streetAddressLine2: address.streetAddressLine2,
    city: address.city,
    state: address.state,
    zip: address.zip,
    latitude: address.latitude === null ? "" : String(address.latitude),
    longitude: address.longitude === null ? "" : String(address.longitude),
  };
}

type AddressSearchFieldBaseProps = {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Fires with the resolved address so the caller can fill the sibling fields
   * (city / state / zip / coordinates). This field owns only the street address.
   */
  onAddressSelect: (address: PlaceAddressFormValues) => void;
};

export type AddressSearchFieldProps = AddressSearchFieldBaseProps &
  FieldBindingProps;

/**
 * Street-address input with Google address search built into the same box:
 * the user types, picks a suggestion, and the rest of the address arrives via
 * `onAddressSelect` — no separate "search address" input above the form.
 */
export function AddressSearchField(props: AddressSearchFieldProps) {
  const {
    label,
    description,
    optional,
    placeholder = "Start typing an address",
    disabled,
    onAddressSelect,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          disabled={disabled}
        >
          <PlacesAutocomplete
            value={field.state.value ?? ""}
            // Typing is free-typing cadence — the real blur below commits it.
            onValueChange={(value) => field.handleChange(value)}
            onPlaceSelect={(place) => {
              const address = toPlaceAddressFormValues(place);
              // The autocomplete leaves its own input on the full one-line
              // description — narrow it back down to just the street address.
              // Picking a suggestion IS a commit, and it must land before the
              // sibling fields are filled from the same address.
              if (address.streetAddress) {
                commitFieldValue(field, address.streetAddress);
              }
              onAddressSelect(address);
            }}
            onBlur={field.handleBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
